// @flow
import { lazy } from 'react'
import enums from 'src/configs/enums'
import ConfigTools from 'src/configs/ConfigTools'

export default ConfigTools({
	RootPage: {
		title: '',
		path: '/',
		component: lazy(() => import('./pages/_RootPage')),
		exact: true,
		permissions: [],
	},
	DashboardPage: {
		title: 'Dashboard',
		path: '/dashboard',
		component: lazy(() =>
			import('src/pages/dashboard/DashboardPage/DashboardPage')
		),
		exact: true,
	},
	StoreHomePage: {
		title: 'Store',
		path: '/procure/store',
		component: lazy(() =>
			import('./pages/Marketplace/Store/StoreHomePage')
		),
		exact: true,
		permissions: [],
		modules: ['Procure'],
	},
	ExploreStorePage: {
		title: 'Search Results',
		path: '/procure/store/explore',
		component: lazy(() =>
			import('./pages/Marketplace/Store/ExploreStorePage')
		),
		exact: true,
		modules: ['Procure'],
	},
	ShopByCategoryPage: {
		title: 'Shop By Category',
		path: '/procure/store/categories',
		component: lazy(() =>
			import('./pages/Marketplace/Store/ShopByCategoryPage')
		),
		exact: true,
		modules: ['Procure'],
	},
	StoreVendorsPage: {
		title: 'My Vendors',
		path: '/procure/store/vendors',
		component: lazy(() =>
			import('./pages/Marketplace/Store/StoreVendorsPage')
		),
		exact: true,
		permissions: [],
		modules: ['Procure'],
	},
	ShopByFormulariesPage: {
		title: 'Shop By Price Books',
		path: '/procure/store/formularies',
		component: lazy(() =>
			import('./pages/Marketplace/Store/ShopByFormulariesPage')
		),
		exact: true,
		permissions: [],
		modules: ['Procure'],
	},
	OffCatalogPage: {
		title: 'Off Catalog',
		path: '/procure/store/off_catalog',
		component: lazy(() =>
			import('./pages/Marketplace/Store/OffCatalogPage')
		),
		exact: true,
		featureKey: 'off_catalog_ordering',
		permissions: [],
		permissionOverride: (authUser) =>
			authUser?.organization?.hasTags(['module:Procure']) &&
			!!authUser?.organization?.off_catalog_status,
		modules: ['Procure'],
	},
	StoreProductDetailsPage: {
		title: 'Product',
		path: '/procure/store/products/:productId',
		component: lazy(() =>
			import('src/pages/store/StoreProductDetailsPage')
		),
		exact: true,
		permissions: [],
		modules: ['Procure'],
	},
	CheckoutPage: {
		title: 'Checkout',
		path: '/procure/store/checkout',
		component: lazy(() => import('./pages/CheckoutPage')),
		exact: true,
		permissions: [],
		modules: ['Procure'],
	},
	CheckoutCompletedPage: {
		title: 'Order Placed',
		path: '/procure/store/checkout/complete',
		component: lazy(() => import('./pages/CheckoutCompletedPage')),
		exact: true,
		permissions: [],
		modules: ['Procure'],
	},
	ShoppingListsPage: {
		title: 'Shopping Lists',
		path: '/procure/store/lists',
		component: lazy(() =>
			import('./pages/Marketplace/ShoppingLists/ShoppingListsPage')
		),
		exact: true,
		featureKey: 'custom_shopping_lists',
		permissions: [],
		modules: ['Procure'],
	},
	ShoppingListDetailsPage: {
		title: 'Shopping List Details',
		path: '/procure/store/lists/:listId',
		component: lazy(() =>
			import('./pages/Marketplace/ShoppingLists/ShoppingListDetailsPage')
		),
		exact: true,
		featureKey: 'custom_shopping_lists',
		permissions: [],
		modules: ['Procure'],
	},
	ManageStorePage: {
		title: 'Banners',
		path: '/procure/store/manage',
		component: lazy(() =>
			import('./pages/Marketplace/Banners/ManageStorePage')
		),
		exact: true,
		featureKey: 'customized_banners',
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Procure']),
		modules: ['Procure'],
	},
	RequestProductPage: {
		title: 'Product Requests',
		path: '/procure/store/catalog/request/product',
		component: lazy(() =>
			import('./pages/Marketplace/Catalog/RequestProductPage')
		),
		exact: true,
		featureKey: 'product_requests',
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'organization-admin',
		],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Procure']),
		modules: ['Procure'],
	},
	StoreVendorDetailsPage: {
		title: 'Vendors',
		path: '/organization/vendors/:vendor_id',
		component: lazy(() =>
			import(
				'./pages/Marketplace/Vendors/StoreVendorDetails/StoreVendorDetailsPage'
			)
		),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'organization-admin',
		],
	},
	ImportVendorProductsPage: {
		title: 'Import Vendor Products',
		path: '/organization/vendors/:vendor_id/import',
		component: lazy(() =>
			import(
				'./pages/Marketplace/Vendors/StoreVendorDetails/ImportVendorProductsPage'
			)
		),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Procure']),
		modules: ['Procure'],
	},
	CartPage: {
		title: 'View Cart',
		path: '/procure/store/cart',
		component: lazy(() => import('./pages/CartPage')),
		exact: true,
		permissions: [],
		modules: ['Procure'],
	},
	OnboardingPage: {
		title: 'Onboarding',
		path: '/onboarding',
		component: lazy(() =>
			import('src/pages/onboarding/OnboardingPage/OnboardingPage')
		),
		exact: true,
		permissions: [
			'curemint-admin',
			'curemint-developer',
			'organization-admin',
		],
	},
	CategoriesListPage: {
		title: 'Categories',
		path: '/admin/categories',
		component: lazy(() => import('./pages/CategoriesListPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
	},
	CategoryDetailsPage: {
		title: 'Categories',
		path: '/admin/categories/:categoryId',
		component: lazy(() => import('./pages/CategoryDetailsPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
	},
	ManageProductRequestsPage: {
		title: 'Product Requests',
		path: '/admin/product-requests',
		component: lazy(() => import('./pages/ManageProductRequestsPage')),
		exact: true,
		permissions: ['curemint-admin', 'curemint-developer'],
	},
	ProductImportPage: {
		title: 'Product Import',
		path: '/admin/products/import',
		component: lazy(() => import('./pages/ProductImportPage')),
		exact: true,
		props: { type: 'vendor' },
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-products',
		],
	},
	BaseProductImportPage: {
		title: 'Base Product Import',
		path: '/admin/products/import/base',
		component: lazy(() => import('./pages/ProductImportPage')),
		exact: true,
		props: { type: 'base' },
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-products',
		],
	},
	CuremintIntegrationListPage: {
		title: 'Integrations',
		path: '/curemint/integrations',
		component: lazy(() => import('./pages/CuremintIntegrationListPage')),
		exact: true,
		permissions: ['curemint-developer'],
	},
	CuremintIntegrationDetailsPage: {
		title: 'Integration Details',
		path: '/curemint/integrations/:integrationId',
		component: lazy(() => import('./pages/CuremintIntegrationDetailsPage')),
		exact: true,
		permissions: ['curemint-developer'],
	},
	InventoryListPage: {
		title: 'Inventory',
		path: '/procure/inventory',
		component: lazy(() => import('./pages/InventoryListPage')),
		exact: true,
		permissions: ['curemint-admin'],
		permissionOverride: (authUser) => {
			return (
				authUser &&
				authUser?.organization?.hasTags(['module:Procure']) &&
				(authUser?.isAdminOfAnyLocation() ||
					authUser?.isPurchaserOfAnyLocation())
			)
		},
		modules: ['Procure'],
	},
	InventoryLocationPage: {
		title: 'Inventory',
		path: '/procure/inventory/location/:locationId',
		component: lazy(() =>
			import('src/navigation/pages/InventoryLocationPage')
		),
		exact: true,
		permissions: ['curemint-admin'],
		permissionOverride: (authUser) => {
			return (
				authUser &&
				authUser?.organization?.hasTags(['module:Procure']) &&
				(authUser?.isAdminOfAnyLocation() ||
					authUser?.isPurchaserOfAnyLocation())
			)
		},
		modules: ['Procure'],
	},
	ProductDuplicateExportPage: {
		title: 'Export Product Duplicates',
		path: '/admin/products/export/duplicates',
		component: lazy(() => import('./pages/ProductExportPage')),
		exact: true,
		props: { type: 'vendor' },
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-products',
		],
	},
	FormularyExportPage: {
		title: 'Export Formulary',
		path: '/admin/products/export/formulary',
		component: lazy(() => import('./pages/FormularyExportPage')),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-products',
		],
	},
	ProductExportPage: {
		title: 'Export Base Product Duplicates',
		path: '/admin/products/export/base/duplicates',
		component: lazy(() => import('./pages/ProductExportPage')),
		exact: true,
		props: { type: 'base' },
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-products',
		],
	},
	ProductDetailsPage: {
		title: 'Products',
		path: '/admin/products/:productId',
		component: lazy(() => import('./pages/ProductDetailsPage')),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-products',
		],
	},
	BaseProductDetailsPage: {
		title: 'Base Product',
		path: '/admin/base_products/:productId',
		component: lazy(() => import('./pages/BaseProductDetailsPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
	},
	ProductsListPage: {
		title: 'Products',
		path: '/admin/products',
		component: lazy(() => import('./pages/ProductsListPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
	},
	CatalogLoadsPage: {
		title: 'Catalog Loads',
		path: '/admin/catalog_loads',
		component: lazy(() =>
			import('src/pages/catalog_loads/CatalogLoadsPage/CatalogLoadsPage')
		),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
	},
	CatalogLoadDetailsPage: {
		title: 'Catalog Loads',
		path: '/admin/catalog_loads/:batchId',
		component: lazy(() =>
			import(
				'src/pages/catalog_loads/CatalogLoadDetails/CatalogLoadDetailsPage'
			)
		),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
	},
	VendorDetailsPage: {
		title: 'Vendors',
		path: '/admin/vendors/:vendorId',
		component: lazy(() => import('./pages/VendorDetailsPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
	},
	VendorsListPage: {
		title: 'Vendors',
		path: '/admin/vendors',
		component: lazy(() => import('./pages/VendorsListPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
	},
	ManufacturerListPage: {
		title: 'Manufacturers',
		path: '/admin/manufacturers',
		component: lazy(() =>
			import(
				'src/pages/manufacturers/ManufacturerListPage/ManufacturerListPage'
			)
		),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-manufacturers',
		],
	},
	ManufacturerDetailsPage: {
		title: 'Manufacturers',
		path: '/admin/manufacturers/:manufacturerId',
		component: lazy(() => import('./pages/ManufacturerDetailsPage')),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-manufacturers',
		],
	},
	OrganizationDetailsPage: {
		title: 'Organizations',
		path: '/admin/organizations/:organizationId',
		component: lazy(() => import('./pages/OrganizationDetailsPage')),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-organizations',
		],
	},
	OrganizationsListPage: {
		title: 'Organizations',
		path: '/admin/organizations',
		component: lazy(() => import('./pages/OrganizationsListPage')),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-organizations',
		],
	},
	AllPriceBooksListPage: {
		title: 'Price Books',
		path: '/admin/formularies',
		component: lazy(() => import('./pages/AllPriceBooksListPage')),
		exact: true,
		featureKey: 'formularies',
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-formularies',
		],
	},
	FormularyDetailsPage: {
		title: 'Price Books',
		path: '/admin/formularies/:formularyId',
		component: lazy(() => import('./pages/FormularyDetailsPage')),
		exact: true,
		featureKey: 'formularies',
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-formularies',
		],
	},
	ImportFormularyPricesPage: {
		title: 'Import Formulary Prices',
		path: '/admin/formularies/:formularyId/vendor/:vendorId/import',
		component: lazy(() => import('./pages/ImportFormularyPricesPage')),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-formularies',
		],
	},
	MyAccountPage: {
		title: 'My Account',
		path: '/my/account',
		component: lazy(() => import('./pages/MyAccountPage')),
		exact: true,
	},
	AllOrdersListPage: {
		title: 'All Orders',
		path: '/admin/orders',
		component: lazy(() => import('./pages/AllOrdersListPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
	},
	AllBillsListPage: {
		title: 'All Bills',
		path: '/cm-admin/bills',
		component: lazy(() => import('./pages/AllBillsListPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
	},
	MyOrganizationOrdersPage: {
		title: 'Orders',
		path: '/procure/store/orders',
		component: lazy(() => import('./pages/MyOrganizationOrdersPage')),
		exact: true,
		modules: ['Procure'],
	},
	MyApprovalsPage: {
		title: 'Approvals',
		path: '/procure/store/approvals',
		component: lazy(() => import('./pages/MyApprovalsPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser &&
			authUser?.organization?.hasTags(['module:Procure']) &&
			(authUser?.isOrganizationAdmin() ||
				authUser?.isAdminOfAnyLocation()),
		modules: ['Procure'],
	},
	ReceivePage: {
		title: 'Receive',
		path: '/procure/store/receive',
		component: lazy(() => import('./pages/ReceivedListPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser &&
			authUser?.organization?.hasTags(['module:Procure']) &&
			(authUser?.isPurchaserOfAnyLocation() ||
				authUser?.isAdminOfAnyLocation()),
		modules: ['Procure'],
	},
	OrderDetailsPage: {
		title: 'Order Details',
		path: '/procure/store/orders/:orderId',
		component: lazy(() => import('./pages/OrderDetailsPage')),
		exact: true,
		permissions: [],
		modules: ['Procure'],
	},
	OrderRulesPage: {
		title: 'Order Rules',
		path: '/procure/store/order_rules',
		component: lazy(() => import('./pages/OrderRulesPage')),
		exact: true,
		featureKey: 'order_rules',
		permissions: [
			'organization-admin',
			'curemint-developer',
			'curemint-admin',
		],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Procure']),
		modules: ['Procure'],
	},
	ManageUsersPage: {
		title: 'User Management',
		path: '/admin/users',
		component: lazy(() => import('./pages/ManageUsersPage')),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'organization-admin',
		],
		permissionOverride: (authUser) =>
			authUser && authUser.isAdminOfAnyLocation(),
	},
	OrganizationVendorListPage: {
		title: 'Vendors',
		path: '/organization/vendors',
		component: lazy(() =>
			import('./pages/MyOrganization/Vendors/OrganizationVendorListPage')
		),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'organization-admin',
		],
	},
	LocationListPage: {
		title: 'Locations',
		path: '/admin/locations',
		component: lazy(() => import('src/navigation/pages/LocationListPage')),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'organization-admin',
		],
		permissionOverride: (authUser) =>
			authUser && authUser.isAdminOfAnyLocation(),
	},
	MyLocationPage: {
		title: 'Locations',
		path: '/admin/my/location',
		component: lazy(() => import('src/navigation/pages/MyLocationPage')),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'organization-admin',
		],
		permissionOverride: (authUser) =>
			authUser && authUser.isAdminOfAnyLocation(),
	},
	LocationDetailsPage: {
		title: 'Location Details',
		path: '/admin/locations/:locationId',
		component: lazy(() =>
			import('src/navigation/pages/LocationDetailsPage')
		),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'organization-admin',
		],
		permissionOverride: (authUser, props) => {
			const locationId = props?.match?.params?.locationId
			return authUser && authUser.isAdminOfLocation(locationId)
		},
	},
	LocationGroupDetailsPage: {
		title: 'Location Group',
		path: '/admin/location_groups/:locationGroupId',
		component: lazy(() =>
			import(
				'src/pages/locations/LocationGroupDetailsPage/LocationGroupDetailsPage'
			)
		),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'organization-admin',
		],
		permissionOverride: (authUser, props) => {
			const locationGroupId = props?.match?.params?.locationGroupId
			return (
				authUser && authUser.isAdminOfAnyLocationGroup(locationGroupId)
			)
		},
	},
	UserGroupDetailsPage: {
		title: 'User Group',
		path: '/admin/user_groups/:userGroupId',
		component: lazy(() => import('./pages/UserGroupDetailsPage')),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'organization-admin',
		],
		permissionOverride: (authUser, props) => {
			const userGroupId = props?.match?.params?.userGroupId

			return authUser && authUser.isAdminOfAnyUserGroup(userGroupId)
		},
	},
	BudgetsPage: {
		title: 'Budgets',
		path: '/admin/budgets',
		component: lazy(() => import('./pages/BudgetsPage')),
		exact: true,
		featureKey: 'budgets',
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) => {
			return (
				!!authUser &&
				authUser?.organization?.hasTags(['module:Procure']) &&
				(authUser?.isAdminOfAnyLocation() ||
					authUser?.isPurchaserOfAnyLocation())
			)
		},
		modules: ['Procure'],
	},
	ImportBudgetsPage: {
		title: 'Import Budgets',
		path: '/admin/budgets/import',
		component: lazy(() => import('./pages/ImportBudgetsPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Procure']),
		modules: ['Procure'],
	},
	MyOrganizationPage: {
		title: 'Organization Settings',
		path: '/admin/settings',
		component: lazy(() => import('./pages/MyOrganizationPage')),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'organization-admin',
		],
	},
	TutorialsPage: {
		title: 'Tutorials',
		path: '/admin/tutorials',
		component: lazy(() => import('./pages/TutorialsPage')),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'organization-admin',
		],
		permissionOverride: (authUser) => {
			return (
				authUser &&
				(authUser.isAdminOfAnyLocation() ||
					authUser?.isAdminOfAnyLocationGroup())
			)
		},
	},
	BillInboxPage: {
		title: 'Bill Inbox',
		path: '/admin/bill-inbox',
		component: lazy(() =>
			import('src/pages/bills/BillInboxPage/BillInboxPage')
		),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			(authUser?.isOrganizationAdmin() || authUser?.canManageBills()) &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	BillsListPage: {
		title: 'Bills',
		path: '/admin/bills',
		component: lazy(() => import('src/pages/bills/BillListPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			(authUser?.isOrganizationAdmin() || authUser?.canViewBills()) &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	BillDetailsPage: {
		title: 'Bills',
		path: '/admin/bills/:billId',
		component: lazy(() =>
			import('src/pages/bills/BillDetailsPage/BillDetailsPage')
		),
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			(authUser?.isOrganizationAdmin() || authUser?.canViewBills()) &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	TranscriptionsPage: {
		title: 'Transcriptions',
		path: '/payables/transcriptions',
		component: lazy(() => import('./pages/TranscriptionsPage')),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'bill-transcriber',
		],
		props: {
			transcriptionType: enums.transcriptionType.TRANSCRIPTION,
		},
	},
	ValidationsPage: {
		title: 'Validations',
		path: '/payables/validations',
		component: lazy(() => import('./pages/TranscriptionsPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin', 'bill-validator'],
		props: {
			transcriptionType: enums.transcriptionType.VALIDATION,
		},
	},
	EscalationsPage: {
		title: 'Escalations',
		path: '/payables/escalations',
		component: lazy(() => import('./pages/TranscriptionsPage')),
		exact: true,
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'bill-transcriber',
			'bill-validator',
			'bill-manager',
		],
		props: {
			transcriptionType: enums.transcriptionType.ESCALATION,
		},
	},
	AccountingCodesListPage: {
		title: 'Accounting',
		path: '/admin/accounting_categories',
		component: lazy(() => import('./pages/AccountingCodesListPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.organization?.hasTags([
				'module:Payables',
				'module:Procure',
			]) && authUser?.canManageBills(),
	},
	ChartOfAccountDetailsPage: {
		title: 'Chart of Accounts Details',
		path: '/admin/accounting_categories/:chartOfAccountId(\\d+)',
		component: lazy(() =>
			import('src/navigation/pages/ChartOfAccountDetailsPage')
		),
		exact: true,
		permissionOverride: (authUser) =>
			authUser?.organization?.hasTags([
				'module:Payables',
				'module:Procure',
			]) && authUser?.canManageBills(),
	},
	LineOfBusinessDetailsPage: {
		title: 'Line of Business Details',
		path: '/admin/lines_of_business/:lineOfBusinessId(\\d+)',
		component: lazy(() =>
			import(
				'src/pages/accounting_categories/LineOfBusinessDetailsPage/LineOfBusinessDetailsPage'
			)
		),
		exact: true,
		modules: ['Procure'],
	},
	UncodedProductsDetailsPage: {
		title: 'Uncoded Products',
		path: '/admin/accounting_categories/uncoded_products',
		component: lazy(() => import('./pages/UncodedProductsDetailsPage')),
		exact: true,
		modules: ['Procure'],
	},
	ImportChartOfAccountsPage: {
		title: 'Import Chart of Accounts Associations',
		path: '/admin/accounting_categories/product/import',
		component: lazy(() =>
			import('src/navigation/pages/ImportChartOfAccountsPage')
		),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Procure']),
		modules: ['Procure'],
	},
	ExplorerPage: {
		title: 'Explorer',
		path: '/explorer',
		component: lazy(() => import('./pages/ExplorerPage')),
		exact: true,
		featureKey: 'data_explorer',
	},
	CustomReportsDownloadPage: {
		title: 'Custom Reports Download',
		path: '/explorer/custom_reports/:id',
		component: lazy(() => import('./pages/CustomReportsDownloadPage')),
		exact: true,
	},
	TierFeatureManagementPage: {
		title: 'Tier Feature Management',
		path: '/tiers/features',
		component: lazy(() => import('./pages/TierFeatureManagementPage')),
		exact: true,
	},
	BankingAccountsPage: {
		title: 'Accounts',
		path: '/admin/finance/accounts',
		component: lazy(() => import('./pages/BankingAccountsPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	BankingStatementsPage: {
		title: 'Statements',
		path: '/admin/finance/statements',
		component: lazy(() => import('./pages/BankingStatementsPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	BankingOAuthRedirectPage: {
		title: 'Banking OAuth Redirect',
		path: '/admin/finance/accounts/oauth',
		component: lazy(() => import('./pages/BankingOAuthRedirectPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	PaymentsListPage: {
		title: 'Payments',
		path: '/admin/finance/payments',
		component: lazy(() => import('./pages/PaymentsPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	CreatePaymentsPage: {
		title: 'Payments',
		path: '/admin/finance/createpayments/:id?',
		component: lazy(() => import('src/pages/payments/CreatePaymentsPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	TransactionsListPage: {
		title: 'Transactions',
		path: '/admin/finance/transactions/:transactionNumber?',
		component: lazy(() => import('./pages/BankingTransactionsPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	OrganizationPriceBookListPage: {
		title: 'Price Books',
		path: '/organization/price-books',
		component: lazy(() => import('./pages/OrganizationPriceBookListPage')),
		exact: true,
		featureKey: 'formularies',
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-formularies',
		],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Procure']),
		modules: ['Procure'],
	},
	ImportPriceBookVendorPricesPage: {
		title: 'Import Price Book Vendor Prices',
		path: '/organization/price-books/:priceBookId/vendor/:vendorId/import',
		component: lazy(() =>
			import('./pages/ImportPriceBookVendorPricesPage')
		),
		exact: true,
		featureKey: 'formularies',
		permissions: [
			'curemint-developer',
			'curemint-admin',
			'curemint-manage-formularies',
		],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Procure']),
		modules: ['Procure'],
	},
	AccountingSoftwarePage: {
		title: 'Integrations',
		path: '/admin/accounting_software',
		component: lazy(() =>
			import(
				'src/pages/accounting_software/AccountingSoftwarePage/AccountingSoftwarePage'
			)
		),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	AccountingSoftwareDetailsPage: {
		title: 'Accounting Software Details',
		path: '/admin/accounting_software/details/:orgEntityId',
		component: lazy(() =>
			import(
				'src/pages/accounting_software/DetailsPage/AccountingSoftwareDetailsPage'
			)
		),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	AccountingSoftwareChartOfAccountsPage: {
		title: 'Accounting Software Chart Of Accounts Page',
		path: '/admin/accounting_software/details/:orgEntityId/chart_of_accounts',
		component: lazy(() =>
			import(
				'src/pages/accounting_software/ChartOfAccountsPage/AccountingSoftwareChartOfAccountsPage'
			)
		),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	AccountingSoftwareVendorsPage: {
		title: 'Accounting Software Vendors Page',
		path: '/admin/accounting_software/details/:orgEntityId/vendors',
		component: lazy(() =>
			import(
				'src/pages/accounting_software/VendorsPage/AccountingSoftwareVendorsPage'
			)
		),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	AccountingSoftwareOtherCategoriesPage: {
		title: 'Accounting Software Other Categories Page',
		path: '/admin/accounting_software/details/:orgEntityId/other_categories',
		component: lazy(() =>
			import(
				'src/pages/accounting_software/OtherCategoriesPage/AccountingSoftwareOtherCategoriesPage'
			)
		),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	ConnectAccountingSoftware: {
		title: 'Connect Accounting Software',
		path: '/admin/accounting_software/details/:orgEntityId/connect/:providerId',
		component: lazy(() =>
			import(
				'src/pages/accounting_software/ConnectAccountingSoftwarePage/ConnectAccountingSoftwarePage'
			)
		),
		exact: true,
		showNav: false,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	ReconnectAccountingSoftware: {
		title: 'Reconnect Accounting Software',
		path: '/admin/accounting_software/details/:orgEntityId/reconnect/:providerId',
		component: lazy(() =>
			import(
				'src/pages/accounting_software/ReconnectAccountingSoftwarePage/ReconnectAccountingSoftwarePage'
			)
		),
		exact: true,
		showNav: false,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	EntitiesPage: {
		title: 'Entities Page',
		path: '/admin/entities',
		component: lazy(() =>
			import('src/pages/entities/EntitiesPage/EntitiesPage')
		),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']),
		modules: ['Payables'],
	},
	EntityDetailsPage: {
		title: 'Entity Details Page',
		path: '/admin/entities/:entityId',
		component: lazy(() =>
			import('src/pages/entities/EntityDetailsPage/EntityDetailsPage')
		),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
		permissionOverride: (authUser) =>
			authUser?.isOrganizationAdmin() &&
			authUser?.organization?.hasTags(['module:Payables']) &&
			authUser?.organization?.hasTags([
				'taggable-feature:Multi-Entity Feature',
			]),
		modules: ['Payables'],
	},
	HubspotIntegrationPage: {
		title: 'HubSpot',
		path: '/curemint/hubspot',
		component: lazy(() => import('./pages/HubspotIntegrationPage')),
		exact: true,
		permissions: ['curemint-developer', 'curemint-admin'],
	},
})
